/**
 * ============================================================================
 * PayoutsStats - KPI CARDS FOR PAYOUT METRICS
 * ============================================================================
 *
 * ⚠️  CRITICAL WARNINGS FOR FUTURE CLAUDE SESSIONS:
 *
 * 1. PURE PRESENTATION COMPONENT
 *    - Receives all data via props
 *    - No calculations or logic
 *    - Parent computes all metrics
 *
 * 2. STATS DISPLAYED
 *    - Total Paid Out (all completed payouts)
 *    - Available Balance (ready to withdraw)
 *    - Held Balance (pending bookings)
 *    - Pending Payouts (processing status)
 *
 * 3. STYLING
 *    - Matches RevenueStats and BookingsManagementNew patterns
 *    - Grid layout: 2 cols mobile, 4 cols desktop
 *    - Icon on right, text on left
 *
 * ============================================================================
 */

import React from 'react';
import { DollarSign, Wallet, Clock, AlertCircle } from 'lucide-react';
import { formatCurrency } from '../../utils/revenueFormatters';

interface PayoutsStatsProps {
  totalPaidOut: number;
  availableBalance: number;
  heldBalance: number;
  pendingPayouts: number;
}

/**
 * Payouts KPI stats cards
 */
export function PayoutsStats({
  totalPaidOut,
  availableBalance,
  heldBalance,
  pendingPayouts,
}: PayoutsStatsProps) {
  const stats = [
    {
      label: 'Total Paid Out',
      value: formatCurrency(totalPaidOut),
      subtext: 'Sent to your bank',
      icon: DollarSign,
      color: 'bg-green-50 text-green-600',
    },
    {
      label: 'Available Balance',
      value: formatCurrency(availableBalance),
      subtext: 'Ready to withdraw',
      icon: Wallet,
      color: 'bg-blue-50 text-blue-600',
    },
    {
      label: 'Held Balance',
      value: formatCurrency(heldBalance),
      subtext: 'Pending bookings',
      icon: Clock,
      color: 'bg-orange-50 text-orange-600',
    },
    {
      label: 'Pending Payouts',
      value: formatCurrency(pendingPayouts),
      subtext: 'Scheduled, not sent',
      icon: AlertCircle,
      color: 'bg-purple-50 text-purple-600',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {stats.map((stat, idx) => (
        <div key={idx} className="bg-white rounded-xl p-3 border border-gray-200">
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-gray-600 text-xs mb-1 truncate">{stat.label}</p>
              <p className="text-lg sm:text-xl font-black text-gray-900 truncate">{stat.value}</p>
              <p className="text-xs text-gray-500 truncate">{stat.subtext}</p>
            </div>
            <div
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${stat.color} flex items-center justify-center flex-shrink-0 ml-2`}
            >
              <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
